import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Package, Plus, ShieldAlert, Menu, Inbox, BookOpen, Settings, Home, LogOut, X, Camera } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const TABS = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Home' },
  { path: '/inventory', icon: Package, label: 'Inventory' },
  { path: '/claims', icon: ShieldAlert, label: 'Claims' },
];

const MORE_LINKS = [
  { path: '/upload?camera=1', icon: Camera, label: 'Take Photos' },
  { path: '/receipt-inbox', icon: Inbox, label: 'Receipt Inbox' },
  { path: '/guides', icon: BookOpen, label: 'Guides' },
  { path: '/settings', icon: Settings, label: 'Settings' },
  { path: '/', icon: Home, label: 'Public site' },
];

/** Bottom tab bar for phones and tablets. The sidebar takes over from lg upwards. */
export const MobileTabBar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const path = location.pathname;

  useEffect(() => { setMoreOpen(false); }, [path]);

  const isActive = (p) => (p === '/claims' ? path.startsWith('/claims') : path === p);
  const moreActive = ['/settings', '/receipt-inbox', '/guides'].some((p) => path.startsWith(p));

  const tabClass = (p) => `tab-item ${isActive(p) ? 'active' : ''}`;

  return (
    <>
      {moreOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="more-sheet">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setMoreOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 animate-slide-up rounded-t-2xl bg-white pb-[env(safe-area-inset-bottom)] shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-200 text-sm font-medium text-slate-600">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
                <p className="truncate text-sm font-medium text-slate-900">{user?.name}</p>
              </div>
              <button
                type="button"
                onClick={() => setMoreOpen(false)}
                aria-label="Close menu"
                className="-mr-2 flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 active:bg-slate-100"
                data-testid="more-sheet-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="px-2 py-2">
              {MORE_LINKS.map(({ path: p, icon: Icon, label }) => (
                <Link
                  key={p}
                  to={p}
                  onClick={() => setMoreOpen(false)}
                  className="flex min-h-[52px] items-center gap-3 rounded-xl px-3 text-base font-medium text-slate-700 active:bg-slate-100"
                  data-testid={`more-nav-${label.toLowerCase().replace(/ /g, '-')}`}
                >
                  <Icon className="h-5 w-5 text-slate-400" />
                  {label}
                </Link>
              ))}
              <button
                type="button"
                onClick={() => logout({ redirectTo: '/' })}
                className="flex min-h-[52px] w-full items-center gap-3 rounded-xl px-3 text-base font-medium text-red-600 active:bg-red-50"
                data-testid="more-nav-sign-out"
              >
                <LogOut className="h-5 w-5" />
                Sign Out
              </button>
            </nav>
          </div>
        </div>
      )}

      <nav className="mobile-tab-bar lg:hidden" aria-label="Primary" data-testid="mobile-tab-bar">
        {TABS.slice(0, 2).map(({ path: p, icon: Icon, label }) => (
          <Link key={p} to={p} className={tabClass(p)} data-testid={`tab-${label.toLowerCase()}`}>
            <Icon className="h-5 w-5" />
            {label}
          </Link>
        ))}

        <Link to="/upload" className={`tab-add ${path === '/upload' ? 'active' : ''}`} aria-label="Add items" data-testid="tab-add">
          <span className="tab-add-circle">
            <Plus className="h-6 w-6" />
          </span>
          Add
        </Link>

        {TABS.slice(2).map(({ path: p, icon: Icon, label }) => (
          <Link key={p} to={p} className={tabClass(p)} data-testid={`tab-${label.toLowerCase()}`}>
            <Icon className="h-5 w-5" />
            {label}
          </Link>
        ))}

        <button type="button" onClick={() => setMoreOpen(true)} className={`tab-item ${moreActive ? 'active' : ''}`} data-testid="tab-more">
          <Menu className="h-5 w-5" />
          More
        </button>
      </nav>
    </>
  );
};

export default MobileTabBar;
